import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";

const Employees = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [teamFilter, setTeamFilter] = useState(searchParams.get("team") || "all");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    skills: "",
    preferred_shifts: [],
    max_hours_per_week: 40,
    team_id: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, shiftRes, teamsRes] = await Promise.all([
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/shifts`, { withCredentials: true }),
        axios.get(`${API}/teams`, { withCredentials: true })
      ]);
      setEmployees(empRes.data);
      setShifts(shiftRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      skills: formData.skills.split(",").map(s => s.trim()).filter(s => s),
      max_hours_per_week: parseInt(formData.max_hours_per_week),
      team_id: formData.team_id || null
    };

    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.employee_id}`, payload, { withCredentials: true });
        toast.success("Employee updated");
      } else {
        await axios.post(`${API}/employees`, payload, { withCredentials: true });
        toast.success("Employee added");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save employee:", error);
      toast.error("Failed to save employee");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || "",
      skills: employee.skills.join(", "),
      preferred_shifts: employee.preferred_shifts || [],
      max_hours_per_week: employee.max_hours_per_week,
      team_id: employee.team_id || null
    });
    setDialogOpen(true);
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    
    try {
      await axios.delete(`${API}/employees/${employeeId}`, { withCredentials: true });
      toast.success("Employee deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete employee:", error);
      toast.error("Failed to delete employee");
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      skills: "",
      preferred_shifts: [],
      max_hours_per_week: 40,
      team_id: teamFilter !== "all" ? teamFilter : null
    });
  };

  const toggleShiftPreference = (shiftId) => {
    setFormData(prev => ({
      ...prev,
      preferred_shifts: prev.preferred_shifts.includes(shiftId)
        ? prev.preferred_shifts.filter(id => id !== shiftId)
        : [...prev.preferred_shifts, shiftId]
    }));
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team?.name || null;
  };

  const getTeamColor = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team?.color || "#52525B";
  };

  const filteredEmployees = teamFilter === "all" 
    ? employees 
    : teamFilter === "unassigned" 
      ? employees.filter(e => !e.team_id)
      : employees.filter(e => e.team_id === teamFilter);

  const getShiftName = (shiftId) => {
    const shift = shifts.find(s => s.shift_id === shiftId);
    return shift?.name || shiftId;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Employees</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Manage your team members and their preferences
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
            data-testid="add-employee-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </div>

        {/* Team Filter */}
        {teams.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">Filter:</span>
            <button
              onClick={() => setTeamFilter("all")}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                teamFilter === "all" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]" : "border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300"
              }`}
              data-testid="filter-all"
            >
              All ({employees.length})
            </button>
            {teams.map((team) => (
              <button
                key={team.team_id}
                onClick={() => setTeamFilter(team.team_id)}
                className={`text-xs px-3 py-1.5 rounded border transition-colors flex items-center gap-1.5 ${
                  teamFilter === team.team_id ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]" : "border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300"
                }`}
                data-testid={`filter-team-${team.team_id}`}
              >
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: team.color }} />
                {team.name} ({employees.filter(e => e.team_id === team.team_id).length})
              </button>
            ))}
            <button
              onClick={() => setTeamFilter("unassigned")}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                teamFilter === "unassigned" ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]" : "border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300"
              }`}
              data-testid="filter-unassigned"
            >
              Unassigned ({employees.filter(e => !e.team_id).length})
            </button>
          </div>
        )}

        {/* Employee List */}
        {filteredEmployees.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No employees yet</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Add your first employee to start building rosters</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">
                    Employee
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4 hidden md:table-cell">
                    Contact
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4 hidden lg:table-cell">
                    Skills
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4 hidden md:table-cell">
                    Team
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4 hidden lg:table-cell">
                    Max Hours
                  </th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-right py-3 px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.employee_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 border border-zinc-200 dark:border-zinc-800">
                          <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-xs font-medium">
                            {getInitials(employee.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{employee.name}</p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 md:hidden">{employee.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">{employee.email}</p>
                      {employee.phone && (
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">{employee.phone}</p>
                      )}
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {employee.skills.slice(0, 3).map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs font-normal">
                            {skill}
                          </Badge>
                        ))}
                        {employee.skills.length > 3 && (
                          <Badge variant="secondary" className="text-xs font-normal">
                            +{employee.skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      {employee.team_id ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: getTeamColor(employee.team_id) }} />
                          <span className="text-sm text-zinc-600 dark:text-zinc-400">{getTeamName(employee.team_id)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-400 dark:text-zinc-500">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <span className="text-sm tabular-nums text-zinc-600 dark:text-zinc-400">
                        {employee.max_hours_per_week}h/week
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(employee)}
                          data-testid={`edit-employee-${employee.employee_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(employee.employee_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-employee-${employee.employee_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                {editingEmployee ? "Edit Employee" : "Add Employee"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div className="grid grid-cols-2 gap-4 sm:col-span-2">
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="employee-name-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="employee-email-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 sm:col-span-2">
                <div>
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1"
                    data-testid="employee-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max_hours" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Max Hours/Week
                  </Label>
                  <Input
                    id="max_hours"
                    type="number"
                    min="1"
                    max="168"
                    value={formData.max_hours_per_week}
                    onChange={(e) => setFormData({ ...formData, max_hours_per_week: e.target.value })}
                    className="mt-1"
                    data-testid="employee-hours-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="skills" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Skills (comma separated)
                </Label>
                <Input
                  id="skills"
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                  placeholder="e.g., Driver, First Aid, Manager"
                  className="mt-1"
                  data-testid="employee-skills-input"
                />
              </div>

              {teams.length > 0 && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Team
                  </Label>
                  <Select
                    value={formData.team_id || "none"}
                    onValueChange={(val) => setFormData({ ...formData, team_id: val === "none" ? null : val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="employee-team-select">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No team</SelectItem>
                      {teams.map((team) => (
                        <SelectItem key={team.team_id} value={team.team_id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: team.color }} />
                            {team.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {shifts.length > 0 && (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2 block">
                    Preferred Shifts
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {shifts.map((shift) => (
                      <button
                        key={shift.shift_id}
                        type="button"
                        onClick={() => toggleShiftPreference(shift.shift_id)}
                        className={`
                          px-3 py-1.5 text-sm rounded border transition-colors
                          ${formData.preferred_shifts.includes(shift.shift_id)
                            ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]'
                            : 'border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300'
                          }
                        `}
                        data-testid={`shift-pref-${shift.shift_id}`}
                      >
                        {shift.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="cancel-employee-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-employee-btn"
                >
                  {editingEmployee ? "Update" : "Add"} Employee
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Employees;
